/**
 * ProseMirror editor controller for `.zwdoc` documents (#249).
 *
 * Binds a view to `.zwdoc` text: parses it once, and hands the text back
 * through `onChange` when the shell flushes (autosave, blur, close,
 * navigation) or when a comment thread changes. Typing marks the draft
 * stale instead of serializing the whole document. The header (synopsis,
 * kind, fields, comment threads) rides along untouched, so Rust can keep
 * splicing its own fields on save.
 */

import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { closeHistory, history } from "prosemirror-history";
import { Fragment, Slice, type Node as PMNode } from "prosemirror-model";
import { EditorState, Plugin, TextSelection } from "prosemirror-state";
import { EditorView, type EditorProps, type NodeView } from "prosemirror-view";
import {
  findNext,
  findPrev,
  replaceAll,
  replaceNext,
  search,
  SearchQuery,
  setSearchState,
} from "prosemirror-search";
import { isJust } from "../../lib/maybe";
import { createTimeoutHandle } from "../../lib/timeoutHandle";
import {
  removeCommentSteps,
  zwDocCommentRanges,
} from "../../lib/zwdoc/comments";
import { zwdocSchema } from "../../lib/zwdoc/schema";
import { newCommentId } from "../commentAnchors";
import { newCommentThread } from "../commentMutations";
import type { CommentSurface } from "../commentSurface";
import type { Maybe } from "../../lib/maybe";
import { ok, type Result } from "../../lib/result";
import {
  parseZwDoc,
  serializeZwDoc,
  type ZwDoc,
  type ZwDocParseError,
} from "../../lib/zwdoc/format";
import {
  NO_EDITOR_CHAPTER_OPENING,
  type EditorChapterOpening,
  type EditorSelectionSnapshot,
  type EditorTextPort,
  type FormatCommand,
} from "../port";
import type { EditorWordRange } from "../wordRange";
import type { EditorImage } from "../port";
import type { EntityPeekEntry } from "../entityPeekIndex";
import {
  applyLink,
  insertImage,
  linkAt,
  proseFormatCommand,
  removeLink,
} from "./commands";
import { proseKeymaps, type ProseShellRequest } from "./keymap";
import { spacerView } from "./spacerView";
import {
  commentAt,
  commentRangesOf,
  commentsPlugin,
  refreshCommentsMeta,
  sentenceRangeAt,
} from "./comments";
import {
  entityPeekPlugin,
  peekHoverSource,
  refreshEntityPeek,
  type ProsePeekContext,
} from "./entityPeek";
import { harperPlugin, lintHoverSource, relintHarper } from "./harper";
import { hoverCardsPlugin } from "./hoverCards";
import { createProsePopover } from "./popover";
import { openingLeadPlugin, refreshOpeningMeta } from "./openingLead";
import { projectionPlugin } from "./projection";
import { historyMarksPlugin, refreshHistoryMarksMeta } from "./historyMarks";
import { paintedSelectionPlugin } from "./selection";
import type { HistoryRange } from "../../lib/proseDiff";
import {
  scrollPosIntoHost,
  setTypewriter,
  typewriterActive,
  typewriterPlugin,
  yieldTypewriter,
  type ScrollHost,
} from "./scroll";
import {
  docPosForTextOffset,
  proseEditorTextPort,
  proseWordOrSelection,
} from "./textTools";

/** Transaction meta: a comment add / remove, allowed while read-only. */
const COMMENT_EDIT = "zwCommentEdit";

/** Breathing room kept around the caret when following it. */
const CARET_TOP_PAD = 48;
const CARET_BOTTOM_PAD = 72;

/**
 * When nothing else will flush the draft, publish it after this pause so
 * word count and the comment margin still catch up. The shell's autosave
 * uses the same delay and publishes on its own, so the two don't stack.
 */
const TEXT_PUBLISH_MS = 1000;

export type ProseEditorOptions = {
  host: HTMLElement;
  text: string;
  editable: boolean;
  /** Document image `src` → URL the webview can load. */
  resolveImageSrc: (src: string) => string;
  /** Outer scroller (`.zw-editor-scroll`); null in tests / before mount. */
  scrollHost: ScrollHost;
  onChange: (text: string) => void;
  /**
   * The document changed and the serialized text is not published yet.
   * Return true when the shell will flush on its own (autosave), which
   * publishes the exact text. Return false and the editor publishes
   * itself after `TEXT_PUBLISH_MS`.
   */
  onDocEdited: () => boolean;
  /** Selection moved, or the editor gained / lost focus. */
  onSelectionChange?: () => void;
  /** Link / image need shell UI (URL dialog, file picker) first. */
  onRequest?: (command: ProseShellRequest) => void;
  /** An image was clicked: open the image toolbar against `element`. */
  onImageClick?: (image: EditorImage, element: HTMLElement) => void;
  /** Harper underlines on at mount (see `setLint`). */
  lint?: boolean;
  /** Entity names to underline at mount (see `setEntityIndex`). */
  entityIndex?: readonly EntityPeekEntry[];
  /** Peek card context; without it entity names underline but don't peek. */
  peek?: ProsePeekContext;
  /** Comment highlights + Add comment on at mount (see `setCommentsEnabled`). */
  comments?: boolean;
  /** A comment was added (or reopened) at the selection: open its thread. */
  onCommentAdded?: (id: string) => void;
  /** Change-history insert / delete marks (plain-text offsets; review only). */
  historyRanges?: readonly HistoryRange[];
  /** Chapter opening to mark at mount (see `setChapterOpening`). */
  chapterOpening?: EditorChapterOpening;
  /**
   * Touch device: soft keyboards (esp. Android) need autocapitalize /
   * autocorrect, or there are no sentence caps and "i" stays lowercase.
   */
  mobile?: boolean;
};

export type { ProsePeekContext, ProseShellRequest };

/** Find bar query. */
export type ProseSearchQuery = {
  search: string;
  replace: string;
  caseSensitive: boolean;
  wholeWord: boolean;
};

export type ProseSearchCommand = "next" | "prev" | "replace" | "replaceAll";

export type ProseEditor = {
  readonly view: EditorView;
  /** Replace the document from outside (file switch, restore). */
  setText(text: string): Result<void, ZwDocParseError>;
  /**
   * Serialize the open document if typing has moved on, and call `onChange`
   * with that exact text. Saves, blur, close and navigation call this so
   * they never write a stale draft. A no-op when nothing is unpublished.
   */
  publishPendingText(): void;
  setEditable(editable: boolean): void;
  /** New image URL resolver (asset root or cache tick changed); re-renders images. */
  setImageResolver(resolve: (src: string) => string): void;
  /** Typewriter scrolling + paragraph dimming (Focus / typewriter pref). */
  setTypewriter(active: boolean): void;
  /** Harper spell + grammar underlines (live writing only). */
  setLint(enabled: boolean): void;
  /** Database entities to underline and peek ([] turns peek off). */
  setEntityIndex(entries: readonly EntityPeekEntry[]): void;
  /**
   * Comment highlights and Add comment. Independent of `editable`, so a
   * comment-only share session can comment on read-only prose.
   */
  setCommentsEnabled(enabled: boolean): void;
  /** For the comment margin, layout and thread actions. */
  readonly commentSurface: CommentSurface;
  /** Change-history marks (plain-text offsets into the projection). */
  setHistoryRanges(ranges: readonly HistoryRange[]): void;
  /** Mark the first paragraph's initial / lead-in (a chapter's first scene). */
  setChapterOpening(opening: EditorChapterOpening): void;
  focus(): void;
  /** False when the command doesn't apply here or isn't supported yet. */
  runCommand(command: FormatCommand): boolean;
  ensureCaretVisible(behavior?: ScrollBehavior): void;
  /** Jump to a plain-text projection offset (occurrences, search). */
  scrollToTextOffset(offset: number): void;
  /** Selection or word at the caret, for the thesaurus seed. */
  wordOrSelection(): Maybe<EditorWordRange>;
  readonly textPort: EditorTextPort;
  selectionSnapshot(): EditorSelectionSnapshot;
  /** Collapse a range selection to its head (bubble dismiss). */
  collapseSelection(): void;
  /** Link under the cursor / selection: its URL, or "" for a new link. */
  linkTarget(): { href: string; editing: boolean };
  /** Apply (or, with "", remove) the link on the selection. */
  applyLink(href: string): boolean;
  removeLink(): boolean;
  /** Insert an image; `alt` defaults to the selected text, then "image". */
  insertImage(src: string, alt?: string): boolean;
  /** Delete `image` if it's still where the toolbar found it. */
  removeImage(image: EditorImage): void;
  /** Set `image`'s alt text; null when it's no longer there. */
  updateImageAlt(image: EditorImage, alt: string): EditorImage | null;
  /** Highlight matches for `query` (empty search clears). */
  setSearch(query: ProseSearchQuery): void;
  /** False when there's no match (or replace while read-only). */
  searchCommand(command: ProseSearchCommand): boolean;
  /** Select the next occurrence of the selection / word at the caret. */
  selectNextOccurrence(): boolean;
  /** Selected text within one block ("" for a caret or multi-block range). */
  selectedText(): string;
  destroy(): void;
};

function imageView(
  node: PMNode,
  resolve: (src: string) => string,
  getPos: () => number | undefined,
  onClick: ProseEditorOptions["onImageClick"],
): NodeView {
  const img = document.createElement("img");
  img.addEventListener("click", () => {
    const pos = getPos();
    if (pos === undefined || onClick === undefined) return;
    onClick(parsedImage(node, pos), img);
  });
  const render = (current: PMNode): void => {
    img.setAttribute("src", resolve(String(current.attrs.src)));
    img.setAttribute("alt", String(current.attrs.alt));
    if (current.attrs.title === "") img.removeAttribute("title");
    else img.setAttribute("title", String(current.attrs.title));
  };
  render(node);
  img.className = "zw-prose-image";
  return {
    dom: img,
    update(next) {
      if (next.type !== node.type) return false;
      render(next);
      return true;
    },
  };
}

/** Paragraph holding only whitespace text (no breaks, images, anchors). */
/**
 * The state with the browser's selection applied. ProseMirror picks up caret
 * moves on `selectionchange`, which fires after the key event, so a shortcut
 * pressed right after an arrow key would otherwise read the previous caret.
 */
function stateWithDomSelection(view: EditorView): EditorState {
  const dom = view.dom.ownerDocument.getSelection();
  if (
    dom === null ||
    dom.anchorNode === null ||
    dom.focusNode === null ||
    view.dom.contains(dom.anchorNode) === false ||
    view.dom.contains(dom.focusNode) === false
  ) {
    return view.state;
  }
  const selection = TextSelection.between(
    view.state.doc.resolve(view.posAtDOM(dom.anchorNode, dom.anchorOffset)),
    view.state.doc.resolve(view.posAtDOM(dom.focusNode, dom.focusOffset)),
  );
  return view.state.apply(view.state.tr.setSelection(selection));
}

function isWhitespaceParagraph(node: PMNode): boolean {
  if (node.type.name !== "paragraph" || node.childCount === 0) return false;
  let blank = true;
  node.forEach((child) => {
    if (child.isText === false || (child.text ?? "").trim() !== "")
      blank = false;
  });
  return blank;
}

/**
 * Pasted HTML (Google Docs wraps blocks in an inline `<b>`) can turn the
 * whitespace between tags into single-space paragraphs. Drop those; keep
 * empty paragraphs and lines with a break, which are deliberate blank lines.
 */
function withoutWhitespaceParagraphs(fragment: Fragment): Fragment {
  const kept: PMNode[] = [];
  fragment.forEach((child) => {
    if (isWhitespaceParagraph(child)) return;
    kept.push(
      child.isTextblock || child.isLeaf
        ? child
        : child.copy(withoutWhitespaceParagraphs(child.content)),
    );
  });
  return Fragment.from(kept);
}

function parsedImage(node: PMNode, pos: number): EditorImage {
  return {
    from: pos,
    to: pos + node.nodeSize,
    src: String(node.attrs.src),
    alt: String(node.attrs.alt),
  };
}

export function createProseEditor(
  options: ProseEditorOptions,
): Result<ProseEditor, ZwDocParseError> {
  const parsed = parseZwDoc(options.text);
  if (parsed.ok === false) return parsed;

  let header = parsed.value.header;
  let lastText = options.text;
  /** Text last handed to `onChange`. An echo of this must not reset the doc. */
  let lastPublished = options.text;
  /** `lastText` is behind `view.state.doc` (or the header). */
  let textStale = false;
  const publishTimer = createTimeoutHandle();
  let editable = options.editable;
  let typewriter = false;
  let resolveImageSrc = options.resolveImageSrc;
  let lint = options.lint ?? false;
  let entityIndex = options.entityIndex ?? [];
  let commentsEnabled = options.comments ?? true;
  /**
   * Read-only with comments on (Reading, comment-only sharing) stays focusable
   * so the caret, selection and Mod-Shift-M work; `readOnlyGuard` drops
   * every other doc change.
   */
  const focusable = (): boolean => editable || commentsEnabled;
  const readOnlyGuard = new Plugin({
    filterTransaction: (tr) =>
      editable || tr.docChanged === false || tr.getMeta(COMMENT_EDIT) === true,
  });
  let openCommentId: string | null = null;
  let historyRanges = options.historyRanges ?? [];
  let chapterOpening = options.chapterOpening ?? NO_EDITOR_CHAPTER_OPENING;
  /**
   * Threads of comments removed in this session, so undoing the removal
   * brings the thread back with its marks (the header isn't in undo history).
   */
  const removedThreads = new Map<string, ZwDoc["header"]["comments"][string]>();
  const flashTimer = createTimeoutHandle();

  // Assigned below; the popover and plugins only read it after mount.
  // eslint-disable-next-line prefer-const
  let view: EditorView;
  const popover = createProsePopover(() => view);
  const hoverSources = [
    lintHoverSource(),
    ...(options.peek === undefined ? [] : [peekHoverSource(options.peek)]),
  ];

  const createState = (doc: PMNode): EditorState =>
    EditorState.create({
      doc,
      plugins: [
        history(),
        paintedSelectionPlugin(),
        ...proseKeymaps(
          (command) => options.onRequest?.(command),
          () => void addComment(),
        ),
        dropCursor(),
        gapCursor(),
        typewriterPlugin(options.scrollHost),
        openingLeadPlugin(() => chapterOpening),
        search(),
        // Projection first: peek and Harper read it; Harper reads peek.
        projectionPlugin(),
        entityPeekPlugin(() => entityIndex),
        readOnlyGuard,
        harperPlugin(() => lint),
        commentsPlugin(() => ({
          enabled: commentsEnabled,
          openId: openCommentId,
          hasThread: (id) => id in header.comments,
        })),
        hoverCardsPlugin(popover, hoverSources),
        historyMarksPlugin(() => historyRanges),
      ],
    });

  const nodeViews = (): NonNullable<EditorProps["nodeViews"]> => ({
    image: (node, _view, getPos) =>
      imageView(node, resolveImageSrc, getPos, options.onImageClick),
    spacer: (node, nodeView, getPos) => spacerView(node, nodeView, getPos),
  });

  view = new EditorView(options.host, {
    state: createState(parsed.value.doc),
    editable: focusable,
    nodeViews: nodeViews(),
    // Native spellcheck off: Harper lints. Writing suggestions off: the OS
    // must not offer inline predictions (Tab accepts them) in the manuscript.
    attributes: {
      class: "zw-prose",
      spellcheck: "false",
      autocomplete: "off",
      writingsuggestions: "false",
      ...(options.mobile === true
        ? { autocapitalize: "sentences", autocorrect: "on" }
        : {}),
    },
    transformPasted: (slice) =>
      new Slice(
        withoutWhitespaceParagraphs(slice.content),
        slice.openStart,
        slice.openEnd,
      ),
    handleDOMEvents: {
      focus: () => {
        options.onSelectionChange?.();
        return false;
      },
      blur: () => {
        options.onSelectionChange?.();
        return false;
      },
    },
    // The outer scroller owns overflow; keep the caret clear of the keyboard /
    // dock insets. Typewriter mode recenters on its own.
    handleScrollToSelection(current) {
      if (typewriterActive(current.state)) return true;
      const scroller = options.scrollHost();
      if (scroller === null) return false;
      scrollPosIntoHost(current, scroller, current.state.selection.head, {
        y: "nearest",
        topMargin: CARET_TOP_PAD,
        bottomMargin: CARET_BOTTOM_PAD,
      });
      return true;
    },
    dispatchTransaction(tr) {
      const headerBefore = header;
      if (tr.docChanged) restoreRemovedThreads(tr.doc);
      const next = view.state.apply(tr);
      view.updateState(next);
      if (tr.selectionSet || tr.docChanged) options.onSelectionChange?.();
      if (tr.docChanged) {
        const commentEdit = tr.getMeta(COMMENT_EDIT) === true;
        noteDocChanged(commentEdit || header !== headerBefore);
      }
    },
  });

  /** Undo brought back a removed comment's marks: give it its thread back. */
  function restoreRemovedThreads(doc: PMNode): void {
    if (removedThreads.size === 0) return;
    let comments = header.comments;
    for (const { id } of zwDocCommentRanges(doc)) {
      const thread = removedThreads.get(id);
      if (thread === undefined || id in comments) continue;
      comments = { ...comments, [id]: thread };
      removedThreads.delete(id);
    }
    if (comments !== header.comments) header = { ...header, comments };
  }

  /** Serialized text, recomputed only when an edit has landed since the last one. */
  const currentText = (): string => {
    if (textStale === false) return lastText;
    lastText = serializeZwDoc({ header, doc: view.state.doc });
    textStale = false;
    return lastText;
  };

  /** Hand the current header + doc to the shell. No-op when nothing is new. */
  const publishPendingText = (): void => {
    publishTimer.clear();
    if (textStale === false) return;
    const text = currentText();
    if (text === lastPublished) return;
    lastPublished = text;
    options.onChange(text);
  };

  /**
   * Record an edit. Comment-thread changes publish immediately so the margin
   * sees the thread. Plain typing waits for the shell's flush, unless the
   * shell isn't going to flush (no save path), in which case we publish
   * ourselves after a pause.
   */
  const noteDocChanged = (immediate: boolean): void => {
    textStale = true;
    const shellWillFlush = options.onDocEdited();
    if (immediate) {
      publishPendingText();
      return;
    }
    if (shellWillFlush) publishTimer.clear();
    else publishTimer.schedule(TEXT_PUBLISH_MS, publishPendingText);
  };

  /** Hand the current header + doc back (header-only changes, e.g. threads). */
  const emitText = (): void => {
    textStale = true;
    publishPendingText();
  };

  const setThreads = (comments: ZwDoc["header"]["comments"]): void => {
    header = { ...header, comments };
  };

  /**
   * Comment the selection, or the sentence at the caret. A caret already in
   * commented text reopens that thread instead.
   */
  const addComment = (): boolean => {
    // Focus mode etc.: still consume the shortcut, insert nothing.
    if (commentsEnabled === false) return true;
    const { state } = view;
    const { from, to, empty } = state.selection;
    if (empty) {
      const existing = commentAt(state, from);
      if (existing !== null && existing in header.comments) {
        const thread = header.comments[existing];
        if (thread !== undefined && thread.resolved) {
          setThreads({
            ...header.comments,
            [existing]: { ...thread, resolved: false },
          });
          emitText();
        }
        options.onCommentAdded?.(existing);
        return true;
      }
    }
    const range = empty ? sentenceRangeAt(state.doc, from) : { from, to };
    if (
      range === null ||
      state.doc.textBetween(range.from, range.to).trim() === ""
    )
      return false;
    const id = newCommentId();
    setThreads({ ...header.comments, [id]: newCommentThread("") });
    const tr = state.tr.addMark(
      range.from,
      range.to,
      zwdocSchema.marks.comment.create({ id }),
    );
    view.dispatch(
      tr
        .setSelection(TextSelection.create(tr.doc, range.from))
        .setMeta(COMMENT_EDIT, true),
    );
    options.onCommentAdded?.(id);
    return true;
  };

  const flashComment = (id: string): void => {
    const spans = view.dom.querySelectorAll(
      `.zw-comment-text[data-comment-id="${CSS.escape(id)}"]`,
    );
    for (const span of spans) span.classList.add("zw-comment-flash");
    flashTimer.schedule(900, () => {
      for (const span of spans) span.classList.remove("zw-comment-flash");
    });
  };

  const commentSurface: CommentSurface = {
    anchorPositions: () =>
      commentRangesOf(view.state).map((range) => ({
        id: range.id,
        pos: range.from,
      })),
    coordsAtPos(pos) {
      try {
        return view.coordsAtPos(
          Math.max(0, Math.min(pos, view.state.doc.content.size)),
        );
      } catch {
        return null;
      }
    },
    contentRect: () => view.dom.getBoundingClientRect(),
    setOpenComment(id) {
      if (id === openCommentId) return;
      openCommentId = id;
      view.dispatch(view.state.tr.setMeta(...refreshCommentsMeta));
    },
    revealComment(id, focus) {
      const range = commentRangesOf(view.state).find((r) => r.id === id);
      if (range === undefined) return;
      yieldTypewriter(view);
      if (focus) {
        view.dispatch(
          view.state.tr.setSelection(
            TextSelection.near(view.state.doc.resolve(range.from)),
          ),
        );
      }
      const scroller = options.scrollHost();
      if (scroller !== null) {
        scrollPosIntoHost(view, scroller, range.from, { y: "center" });
      }
      flashComment(id);
    },
    flashComment,
    removeComment(id) {
      const tr = removeCommentSteps(view.state.tr, id);
      if (id in header.comments === false && tr.docChanged === false)
        return false;
      const { [id]: removed, ...rest } = header.comments;
      if (removed !== undefined) removedThreads.set(id, removed);
      setThreads(rest);
      if (tr.docChanged) view.dispatch(tr.setMeta(COMMENT_EDIT, true));
      else emitText();
      return true;
    },
    // Positions come straight from the DOM; nothing cached to re-measure.
    requestMeasure: () => {},
  };

  return ok({
    view,
    publishPendingText,
    commentSurface,
    textPort: proseEditorTextPort(
      () => view,
      () => editable,
    ),
    setText(text) {
      // The shell echoing the text we just published. Later keystrokes may
      // already be in the document; don't replace them with that snapshot.
      if (text === lastPublished) return ok(undefined);
      const next = parseZwDoc(text);
      if (next.ok === false) return next;
      publishTimer.clear();
      header = next.value.header;
      lastText = text;
      lastPublished = text;
      textStale = false;
      // Same prose (e.g. Rust rewrote only the header): keep selection + undo,
      // and re-check which comments have threads.
      if (next.value.doc.eq(view.state.doc)) {
        view.dispatch(view.state.tr.setMeta(...refreshCommentsMeta));
        return ok(undefined);
      }
      const state = createState(next.value.doc);
      const selection = TextSelection.near(
        state.doc.resolve(
          Math.min(view.state.selection.head, state.doc.content.size),
        ),
      );
      view.updateState(state.apply(state.tr.setSelection(selection)));
      if (typewriter) setTypewriter(view, true);
      return ok(undefined);
    },
    setEditable(next) {
      editable = next;
      view.setProps({ editable: focusable });
    },
    setImageResolver(resolve) {
      resolveImageSrc = resolve;
      view.setProps({
        nodeViews: nodeViews(),
      });
    },
    setTypewriter(active) {
      typewriter = active;
      setTypewriter(view, active);
    },
    setLint(enabled) {
      if (enabled === lint) return;
      lint = enabled;
      relintHarper(view);
    },
    setChapterOpening(opening) {
      if (
        opening.initial === chapterOpening.initial &&
        opening.leadIn === chapterOpening.leadIn
      ) {
        return;
      }
      chapterOpening = opening;
      view.dispatch(view.state.tr.setMeta(...refreshOpeningMeta));
    },
    setHistoryRanges(ranges) {
      historyRanges = ranges;
      view.dispatch(view.state.tr.setMeta(...refreshHistoryMarksMeta));
    },
    setCommentsEnabled(enabled) {
      if (enabled === commentsEnabled) return;
      commentsEnabled = enabled;
      view.setProps({ editable: focusable });
      view.dispatch(view.state.tr.setMeta(...refreshCommentsMeta));
    },
    setEntityIndex(entries) {
      entityIndex = entries;
      refreshEntityPeek(view);
      // Entity names are skipped by Harper; drop / restore their underlines.
      relintHarper(view);
    },
    focus() {
      view.focus();
    },
    runCommand(command) {
      if (command === "addComment") return addComment();
      if (editable === false) return false;
      if (command === "link" || command === "image") {
        options.onRequest?.(command);
        return options.onRequest !== undefined;
      }
      const proseCommand = proseFormatCommand(command);
      if (proseCommand === null) return false;
      // Ribbon / menu formatting is its own undo step, never merged with
      // the typing just before it.
      const ran = proseCommand(
        view.state,
        (tr) => view.dispatch(closeHistory(tr)),
        view,
      );
      if (ran) view.focus();
      return ran;
    },
    ensureCaretVisible(behavior = "auto") {
      const scroller = options.scrollHost();
      if (scroller === null || typewriterActive(view.state)) return;
      scrollPosIntoHost(view, scroller, view.state.selection.head, {
        y: "nearest",
        topMargin: CARET_TOP_PAD,
        bottomMargin: CARET_BOTTOM_PAD,
        behavior,
      });
    },
    scrollToTextOffset(offset) {
      yieldTypewriter(view);
      const pos = docPosForTextOffset(view.state, offset);
      view.dispatch(
        view.state.tr.setSelection(
          TextSelection.near(view.state.doc.resolve(pos)),
        ),
      );
      const scroller = options.scrollHost();
      if (scroller !== null) {
        scrollPosIntoHost(view, scroller, pos, { y: "start", yMargin: 48 });
      }
      view.focus();
    },
    wordOrSelection() {
      return proseWordOrSelection(stateWithDomSelection(view));
    },
    selectionSnapshot() {
      const { empty, from, to } = view.state.selection;
      return {
        empty,
        from,
        to,
        hasFocus: view.hasFocus(),
        coordsAtPos: (pos) => {
          try {
            return view.coordsAtPos(pos);
          } catch {
            return null;
          }
        },
      };
    },
    linkTarget() {
      const link = linkAt(view.state);
      return { href: link?.href ?? "", editing: link !== null };
    },
    applyLink(href) {
      if (editable === false) return false;
      const ran = applyLink(href)(view.state, view.dispatch);
      if (ran) view.focus();
      return ran;
    },
    removeLink() {
      if (editable === false) return false;
      const ran = removeLink()(view.state, view.dispatch);
      if (ran) view.focus();
      return ran;
    },
    insertImage(src, alt) {
      if (editable === false) return false;
      const { from, to } = view.state.selection;
      const selected = view.state.doc.textBetween(from, to).trim();
      const label = alt ?? (selected === "" ? "image" : selected);
      const ran = insertImage(src, label)(view.state, view.dispatch);
      if (ran) view.focus();
      return ran;
    },
    removeImage(image) {
      const node = view.state.doc.nodeAt(image.from);
      if (node?.type.name !== "image" || String(node.attrs.src) !== image.src)
        return;
      view.dispatch(
        view.state.tr.delete(image.from, image.from + node.nodeSize),
      );
      view.focus();
    },
    updateImageAlt(image, alt) {
      const node = view.state.doc.nodeAt(image.from);
      if (node?.type.name !== "image" || String(node.attrs.src) !== image.src)
        return null;
      view.dispatch(
        view.state.tr.setNodeMarkup(image.from, undefined, {
          ...node.attrs,
          alt,
        }),
      );
      const updated = view.state.doc.nodeAt(image.from);
      return updated === null ? null : parsedImage(updated, image.from);
    },
    setSearch(query) {
      view.dispatch(setSearchState(view.state.tr, new SearchQuery(query)));
    },
    searchCommand(command) {
      const run = {
        next: findNext,
        prev: findPrev,
        replace: replaceNext,
        replaceAll,
      }[command];
      if (
        (command === "replace" || command === "replaceAll") &&
        editable === false
      ) {
        return false;
      }
      yieldTypewriter(view);
      const ran = run(view.state, view.dispatch, view);
      // ProseMirror only scrolls to the selection while the DOM selection is
      // in the editor; with focus in the find field, scroll the match here.
      const scroller = options.scrollHost();
      if (ran && scroller !== null) {
        scrollPosIntoHost(view, scroller, view.state.selection.from, {
          y: "center",
        });
      }
      return ran;
    },
    selectedText() {
      if (view.state.selection.empty) return "";
      const word = proseWordOrSelection(view.state);
      return isJust(word) ? word.value.text : "";
    },
    selectNextOccurrence() {
      const word = proseWordOrSelection(view.state);
      if (isJust(word) === false) return false;
      const { text, to } = word.value;
      view.dispatch(
        setSearchState(
          view.state.tr.setSelection(TextSelection.create(view.state.doc, to)),
          new SearchQuery({ search: text, caseSensitive: true }),
        ),
      );
      return findNext(view.state, view.dispatch, view);
    },
    collapseSelection() {
      const { head } = view.state.selection;
      view.dispatch(
        view.state.tr.setSelection(TextSelection.create(view.state.doc, head)),
      );
    },
    destroy() {
      flashTimer.clear();
      publishTimer.clear();
      view.destroy();
      popover.destroy();
    },
  });
}
